import React, { useCallback, useState } from "react";
import { Redirect } from "react-router-dom";
import { useMutation } from "@apollo/client";
import MatchupAdvantageTable from "./MatchupAdvantageTable";
import { UPLOAD_DEFENSE_CSV, UPLOAD_OFFENSE_CSV } from "./mutations";
import NFLToolLayout from "../__shared/NFLToolLayout";
import SegmentedControl from "../__shared/SegmentedControl";
import AdminUploadPanel from "../__shared/AdminUploadPanel";
import ToolStatePanel, { TableSkeleton } from "../__shared/ToolStatePanel";
import ToolErrorBoundary from "../__shared/ToolErrorBoundary";
import useNflReference from "../__shared/useNflReference";
import { isSiteAdmin, isToolSub } from "../../../../../AuthUtils";
import "./NFLMatchupAdvantagePage.css";

const TITLE = "Matchup Advantage";
const SUBTITLE = "How each offense and defense matches up this week.";
const VIEWS = [
  { value: "offense", label: "Offense" },
  { value: "defense", label: "Defense" },
];
const OFFENSE_COLUMNS = [
  "Team",
  "Opp",
  "Game Total",
  "Team Total",
  "Run Game Rank",
  "Pass Game Rank",
  "Team Onslaught Rank",
];
const DEFENSE_COLUMNS = ["Team", "Opp", "Game Total", "Team Total", "Defense Rank"];

export default function NFLMatchupAdvantagePage() {
  const [referenceFetchKey, setReferenceFetchKey] = useState(0);

  return (
    <ToolErrorBoundary
      what="Rankings"
      onRetry={() => setReferenceFetchKey((key) => key + 1)}
      wrapError={(panel) => (
        <NFLToolLayout tool="matchup-advantage" title={TITLE} subtitle={SUBTITLE}>
          {panel}
        </NFLToolLayout>
      )}
    >
      <React.Suspense
        fallback={
          <NFLToolLayout tool="matchup-advantage" title={TITLE} subtitle={SUBTITLE}>
            <TableSkeleton columns={OFFENSE_COLUMNS} />
          </NFLToolLayout>
        }
      >
        <MatchupTool referenceFetchKey={referenceFetchKey} />
      </React.Suspense>
    </ToolErrorBoundary>
  );
}

function MatchupTool({ referenceFetchKey }) {
  const reference = useNflReference({ fetchKey: referenceFetchKey });
  const [view, setView] = useState("offense");
  const [fetchKey, setFetchKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);

  const [uploadOffensiveCSV] = useMutation(UPLOAD_OFFENSE_CSV);
  const [uploadDefensiveCSV] = useMutation(UPLOAD_DEFENSE_CSV);

  const offense = view === "offense";

  const retry = useCallback(() => setFetchKey((key) => key + 1), []);

  const upload = useCallback(
    (file) => {
      const run = offense ? uploadOffensiveCSV : uploadDefensiveCSV;
      const fieldName = offense
        ? "uploadOffensiveMatchupRankings"
        : "uploadDefensiveMatchupRankings";
      return run({ variables: { csv: file } }).then((result) => {
        const payload = result && result.data && result.data[fieldName];
        if (!payload || !payload.success) {
          throw new Error(
            `Upload failed. ${offense ? "Offensive" : "Defensive"} rankings were not updated.`
          );
        }
        setFetchKey((key) => key + 1);
      });
    },
    [offense, uploadOffensiveCSV, uploadDefensiveCSV]
  );

  const canUpload =
    isSiteAdmin(reference.groups) || reference.username === "hahny0l0";

  if (!isToolSub(reference.groups)) {
    return <Redirect to="/memberships" />;
  }

  const currentWeek = reference.currentSeason
    ? reference.currentSeason.currentWeek
    : null;
  const side = offense ? "offensive" : "defensive";

  const emptyState = (
    <ToolStatePanel
      badge={currentWeek}
      title={
        currentWeek
          ? `No ${side} rankings for week ${currentWeek} yet`
          : `No ${side} rankings yet`
      }
      body={
        canUpload ? "Rankings you upload show up here right away." : undefined
      }
    />
  );

  return (
    <NFLToolLayout
      tool="matchup-advantage"
      title={TITLE}
      subtitle={SUBTITLE}
      actions={
        <>
          <SegmentedControl
            label="View"
            options={VIEWS}
            isSelected={(value) => value === view}
            onSelect={setView}
          />
          {canUpload ? (
            <button
              type="button"
              className="nfl-btn"
              aria-expanded={uploadOpen}
              onClick={() => setUploadOpen((open) => !open)}
            >
              Upload rankings
            </button>
          ) : null}
        </>
      }
    >
      {canUpload ? (
        <AdminUploadPanel
          open={uploadOpen}
          title={`Upload ${side} matchup rankings`}
          buttonLabel="Upload rankings"
          onUpload={upload}
          onClose={() => setUploadOpen(false)}
        />
      ) : null}

      <ToolErrorBoundary what="Rankings" onRetry={retry}>
        <React.Suspense
          fallback={
            <TableSkeleton columns={offense ? OFFENSE_COLUMNS : DEFENSE_COLUMNS} />
          }
        >
          <MatchupAdvantageTable
            key={view}
            offense={offense}
            fetchKey={fetchKey}
            emptyState={emptyState}
          />
        </React.Suspense>
      </ToolErrorBoundary>
    </NFLToolLayout>
  );
}
